import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { TbRefresh, TbInfoCircle, TbCheck, TbX } from "react-icons/tb";
import { RegisterFormData } from "../registerTypes";
import { apiRequest } from "src/services/apiClient";
import { PasswordInput } from "src/components/PasswordInput/PasswordInput";

type IdStatus = "idle" | "checking" | "available" | "taken";

interface Props {
    data: RegisterFormData;
    setData: React.Dispatch<React.SetStateAction<RegisterFormData>>;
    onIdStatus?: (available: boolean | null) => void;
}

// Transliterate common diacritics to ASCII equivalents
function transliterate(s: string): string {
    return s
        .replace(/[äÄ]/g, "ae").replace(/[öÖ]/g, "oe").replace(/[üÜ]/g, "ue")
        .replace(/ß/g, "ss").replace(/[àáâãå]/gi, "a").replace(/[èéêë]/gi, "e")
        .replace(/[ìíîï]/gi, "i").replace(/[òóôõ]/gi, "o").replace(/[ùúûü]/gi, "u")
        .replace(/[ýÿ]/gi, "y").replace(/[ñ]/gi, "n").replace(/[ç]/gi, "c")
        .replace(/[^a-zA-Z0-9\s]/g, " ");
}

const SKIP_WORDS = new Set([
    "dr", "drs", "prof", "med", "mr", "mrs", "ms",
    "praxis", "arzt", "klinik", "zentrum", "herr", "frau",
    "the", "and", "und",
]);

function generateSuggestions(name: string): string[] {
    const words = transliterate(name)
        .trim()
        .split(/\s+/)
        .map(w => w.toLowerCase().replace(/[^a-z0-9]/g, ""))
        .filter(w => w.length > 0 && !SKIP_WORDS.has(w));

    if (words.length === 0) return ["praxis"];

    const results: string[] = [];
    const seen = new Set<string>();

    const add = (s: string) => {
        const clean = s.replace(/[^a-z0-9]/gi, "").slice(0, 16);
        if (clean.length >= 2 && !seen.has(clean.toLowerCase())) {
            seen.add(clean.toLowerCase());
            results.push(clean);
        }
    };

    const first = words[0];
    const last  = words.length > 1 ? words[words.length - 1] : "";
    const fi    = first[0] ?? "";
    const li    = last[0]  ?? "";

    if (first && last) {
        add(`${first}${last}`);                                    // chrischatzi
        add(`${fi}${last}`);                                       // cchatzi
        add(`${first}${li.toUpperCase()}`);                        // chrisC
        add(`praxis${fi.toUpperCase()}${li.toUpperCase()}`);       // praxisCC
        add(last);                                                  // chatzi
    } else {
        add(first);                                                 // muller
        add(`praxis${first.slice(0, 8)}`);                         // praxismuller
        add(`${first.slice(0, 8)}praxis`);                         // mullerpraxis
        add(`${fi.toUpperCase()}${first.slice(1, 10)}`);           // Muller
    }

    return results.slice(0, 5);
}

export default function StepPraxis({ data, setData, onIdStatus }: Props) {
    const { t } = useTranslation();
    const [idStatus, setIdStatus] = useState<IdStatus>("idle");
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const checkId = useCallback(async (id: string) => {
        const trimmed = id.trim();
        if (!trimmed) { setIdStatus("idle"); onIdStatus?.(null); return; }
        setIdStatus("checking");
        try {
            const json = await apiRequest<{ available: boolean }>(
                `/check-praxis-id?id=${encodeURIComponent(trimmed)}`
            );
            const status: IdStatus = json.available ? "available" : "taken";
            setIdStatus(status);
            onIdStatus?.(json.available);
        } catch {
            setIdStatus("idle");
            onIdStatus?.(null);
        }
    }, [onIdStatus]);

    const scheduleCheck = (id: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => checkId(id), 500);
    };

    const set = <K extends keyof RegisterFormData>(k: K, v: RegisterFormData[K]) =>
        setData(d => ({ ...d, [k]: v }));

    const handleNameChange = (name: string) => {
        setData(d => {
            const newId = d.praxisIdManual ? d.praxisId : (generateSuggestions(name)[0] ?? "");
            if (!d.praxisIdManual) scheduleCheck(newId);
            return { ...d, praxisName: name, praxisId: newId };
        });
    };

    const suggestions = generateSuggestions(data.praxisName);

    return (
        <div className="register-step">

            {/* Practice name */}
            <div className="form-group">
                <label>{t("register.praxisName")} *</label>
                <input
                    type="text"
                    value={data.praxisName}
                    onChange={e => handleNameChange(e.target.value)}
                    placeholder="z.B. Praxis Dr. Müller"
                    autoFocus
                />
            </div>

            {/* Admin first + last name */}
            <div className="register-step__row">
                <div className="form-group register-step__half">
                    <label>{t("register.firstName")} *</label>
                    <input
                        type="text"
                        value={data.adminFirstName}
                        onChange={e => set("adminFirstName", e.target.value)}
                        placeholder="z.B. Max"
                    />
                </div>
                <div className="form-group register-step__half">
                    <label>{t("register.lastName")} *</label>
                    <input
                        type="text"
                        value={data.adminLastName}
                        onChange={e => set("adminLastName", e.target.value)}
                        placeholder="z.B. Müller"
                    />
                </div>
            </div>

            {/* Email */}
            <div className="form-group">
                <label>{t("register.praxisEmail")} *</label>
                <input
                    type="email"
                    value={data.praxisEmail}
                    onChange={e => set("praxisEmail", e.target.value)}
                    placeholder="z.B. kontakt@praxis.de"
                />
            </div>

            {/* Password row */}
            <div className="register-step__row">
                <div className="form-group register-step__half">
                    <label>{t("register.password")} *</label>
                    <PasswordInput
                        value={data.praxisPassword}
                        onChange={e => set("praxisPassword", e.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                    />
                </div>
                <div className="form-group register-step__half">
                    <label>{t("register.confirmPassword")} *</label>
                    <PasswordInput
                        value={data.praxisConfirmPassword}
                        onChange={e => set("praxisConfirmPassword", e.target.value)}
                        placeholder="••••••••"
                        autoComplete="new-password"
                    />
                </div>
            </div>
            <p className="register-step__password-hint">{t("register.passwordHint")}</p>

            {/* Login ID */}
            <div className="register-step__row">
                <div className="form-group">
                    <label>{t("register.suggestedId")} *</label>

                    <div className="register-step__id-row">
                        <input
                            type="text"
                            value={data.praxisId}
                            onChange={e => {
                                const v = e.target.value;
                                setData(d => ({ ...d, praxisId: v, praxisIdManual: true }));
                                setIdStatus("idle");
                                scheduleCheck(v);
                            }}
                        />
                        <button
                            type="button"
                            className="register-step__icon-btn"
                            onClick={() => {
                                const newId = generateSuggestions(data.praxisName)[0] ?? "";
                                setData(d => ({ ...d, praxisId: newId, praxisIdManual: false }));
                                scheduleCheck(newId);
                            }}
                            title={t("register.regenerate")}
                        >
                            <TbRefresh />
                        </button>
                        {idStatus === "checking" && (
                            <span className="register-id-status register-id-status--checking">
                                {t("register.idChecking")}
                            </span>
                        )}
                        {idStatus === "available" && (
                            <span className="register-id-status register-id-status--available">
                                <TbCheck /> {t("register.idAvailable")}
                            </span>
                        )}
                        {idStatus === "taken" && (
                            <span className="register-id-status register-id-status--taken">
                                <TbX /> {t("register.idTaken")}
                            </span>
                        )}
                    </div>

                    {/* Suggestion chips */}
                    {suggestions.length > 0 && (
                        <div className="register-id-suggestions">
                            <span className="register-id-suggestions__label">{t("register.suggestedIds")}</span>
                            {suggestions.map(s => (
                                <button
                                    key={s}
                                    type="button"
                                    className={`register-id-chip${data.praxisId === s ? " register-id-chip--active" : ""}`}
                                    onClick={() => {
                                        setData(d => ({ ...d, praxisId: s, praxisIdManual: true }));
                                        scheduleCheck(s);
                                    }}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Login notice */}
                    <div className="register-login-notice">
                        <TbInfoCircle />
                        <span>{t("register.praxisIdNotice")}</span>
                    </div>
                </div>
            </div>

        </div>
    );
}
